import type * as React from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';

import { cn } from '../../styles/lib/utils';
import { FieldContent, FieldDescription } from '../Field';
import { Switch, type SwitchProps } from '../Switch';
import { FormControl, FormField, type FormFieldProps, FormItem, FormLabel, FormMessage } from './Form';

// `isRequired` is omitted because FormField maps it to `required`, which rejects `false` and would mean "must be on".
type FormSwitchProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  FormFieldProps<TFieldValues, TName>,
  'render' | 'disabled' | 'isRequired'
> & {
  label?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  tooltipText?: React.ReactNode;
  className?: string;
  switchProps?: Omit<SwitchProps, 'checked' | 'onCheckedChange' | 'name' | 'id' | 'disabled' | 'data-testid'>;
  'data-testid'?: string;
  itemTestId?: string;
  labelTestId?: string;
};

function FormSwitch<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  label,
  description,
  disabled,
  tooltipText,
  className,
  switchProps,
  'data-testid': testId,
  itemTestId,
  labelTestId,
  ...fieldProps
}: FormSwitchProps<TFieldValues, TName>) {
  return (
    <FormField
      {...fieldProps}
      render={({ field, fieldState, id, messageId }) => {
        const descriptionId = `${id}-description`;
        const describedBy =
          [description ? descriptionId : null, fieldState.error?.message ? messageId : null]
            .filter(Boolean)
            .join(' ') || undefined;

        return (
          <FormItem
            orientation="horizontal"
            className={cn('justify-between gap-3 has-[>[data-slot=field-content]]:items-center', className)}
            data-testid={itemTestId}
          >
            <FieldContent className="gap-0">
              {label && (
                <FormLabel
                  tooltipText={tooltipText}
                  tooltipTestId={testId ? `${testId}-info` : undefined}
                  data-testid={labelTestId}
                >
                  {label}
                </FormLabel>
              )}
              {description && <FieldDescription id={descriptionId}>{description}</FieldDescription>}
              <FormMessage />
            </FieldContent>
            <FormControl>
              {/* The child's aria-describedby wins over FormControl's, so it carries the description id too. */}
              <Switch
                checked={!!field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                disabled={disabled}
                {...switchProps}
                aria-describedby={describedBy}
                data-testid={testId}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
}

export { FormSwitch };
export type { FormSwitchProps };
